import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GridColDef } from "@mui/x-data-grid";

import { LojaDataGrid } from "layouts/pages/loja/common";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ConfirmDialog from "components/ConfirmDialog";
import { setMessage, useMaterialUIController } from "context";
import { formatDate } from "helpers/formatDate";
import {
  CANAL_LABEL,
  ComunicadoCampanhaStatus,
  ENVIO_STATUS_LABEL,
  IComunicadoCampanha,
  IComunicadoEnvio,
  PUBLICO_LABEL,
  STATUS_LABEL,
} from "interfaces/comunicado";
import {
  cancelarComunicado,
  listarEnviosComunicado,
  obterComunicado,
} from "services/comunicados";

const PENDING_STATUSES: ComunicadoCampanhaStatus[] = ["ENFILEIRADO", "PROCESSANDO"];
const POLL_INTERVAL_MS = 3000;

function statusColor(status: ComunicadoCampanhaStatus) {
  switch (status) {
    case "CONCLUIDO":
      return "success";
    case "PROCESSANDO":
    case "ENFILEIRADO":
      return "info";
    case "ERRO":
      return "error";
    case "CANCELADO":
      return "default";
    default:
      return "warning";
  }
}

function envioStatusColor(status: string) {
  switch (status) {
    case "ENVIADO":
      return "success";
    case "FALHA":
      return "error";
    default:
      return "default";
  }
}

type Props = {
  open: boolean;
  campanhaId: string | null;
  onClose: () => void;
  onUpdated: () => void;
};

export default function ComunicadoDetalheDrawer({
  open,
  campanhaId,
  onClose,
  onUpdated,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [campanha, setCampanha] = useState<IComunicadoCampanha | null>(null);
  const [envios, setEnvios] = useState<IComunicadoEnvio[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingEnvios, setLoadingEnvios] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const prevStatusRef = useRef<ComunicadoCampanhaStatus | null>(null);

  const loadCampanha = useCallback(
    async (silent = false) => {
      if (!campanhaId) return;
      if (!silent) setLoading(true);
      try {
        const data = await obterComunicado(campanhaId);
        setCampanha(data);
      } catch (error: unknown) {
        if (!silent) {
          setMessage(dispatch, {
            open: true,
            severity: "error",
            text: error instanceof Error ? error.message : "Falha ao carregar.",
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [campanhaId, dispatch],
  );

  const loadEnvios = useCallback(async (silent = false) => {
    if (!campanhaId) return;
    if (!silent) setLoadingEnvios(true);
    try {
      const data = await listarEnviosComunicado(campanhaId, {
        page: 1,
        limit: 100,
      });
      setEnvios(data.items);
    } catch {
      setEnvios([]);
    } finally {
      if (!silent) setLoadingEnvios(false);
    }
  }, [campanhaId]);

  const refreshDetalhe = useCallback(
    async (silent = false) => {
      await Promise.all([loadCampanha(silent), loadEnvios(silent)]);
    },
    [loadCampanha, loadEnvios],
  );

  useEffect(() => {
    if (open && campanhaId) {
      prevStatusRef.current = null;
      void refreshDetalhe();
    } else {
      setCampanha(null);
      setEnvios([]);
      prevStatusRef.current = null;
    }
  }, [open, campanhaId, refreshDetalhe]);

  const campanhaPendente =
    campanha != null && PENDING_STATUSES.includes(campanha.status);

  useEffect(() => {
    if (!open || !campanhaId || !campanhaPendente) return;

    const interval = window.setInterval(() => {
      void refreshDetalhe(true);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [open, campanhaId, campanhaPendente, refreshDetalhe]);

  useEffect(() => {
    if (!campanha) return;

    const prev = prevStatusRef.current;
    if (
      prev &&
      PENDING_STATUSES.includes(prev) &&
      !PENDING_STATUSES.includes(campanha.status)
    ) {
      onUpdated();
    }
    prevStatusRef.current = campanha.status;
  }, [campanha, onUpdated]);

  const podeCancelar = campanhaPendente;

  const handleCancel = async () => {
    if (!campanhaId) return;
    setCanceling(true);
    try {
      await cancelarComunicado(campanhaId);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Campanha cancelada.",
      });
      onUpdated();
      void loadCampanha();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao cancelar.",
      });
    } finally {
      setCanceling(false);
      setConfirmCancel(false);
    }
  };

  const envioColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "canal",
        headerName: "Canal",
        width: 80,
      },
      {
        field: "destino",
        headerName: "Destino",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "status",
        headerName: "Status",
        width: 110,
        renderCell: (params) => (
          <Chip
            size="small"
            label={ENVIO_STATUS_LABEL[params.value as keyof typeof ENVIO_STATUS_LABEL]}
            color={envioStatusColor(params.value as string) as "success" | "error" | "default"}
          />
        ),
      },
      {
        field: "enviadoEm",
        headerName: "Data",
        width: 140,
        valueFormatter: (params) =>
          params.value ? formatDate(params.value as string, "dd/MM/yyyy HH:mm") : "",
      },
      {
        field: "erro",
        headerName: "Erro",
        flex: 1,
        minWidth: 120,
      },
    ],
    [],
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: "100%", md: "720px" }, p: 0 },
        }}
      >
        <MDBox p={2}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <MDTypography variant="h5" fontWeight="medium">
              Detalhe da campanha
            </MDTypography>
            <IconButton onClick={onClose} aria-label="Fechar">
              <CloseIcon />
            </IconButton>
          </MDBox>

          {loading && !campanha ? (
            <MDTypography variant="body2">Carregando...</MDTypography>
          ) : campanha ? (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                <Chip
                  label={STATUS_LABEL[campanha.status]}
                  color={statusColor(campanha.status) as "success" | "info" | "error" | "warning" | "default"}
                />
                <Chip label={CANAL_LABEL[campanha.canal]} size="small" />
                <Chip label={PUBLICO_LABEL[campanha.publico]} size="small" variant="outlined" />
              </Stack>

              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                {campanha.tituloInterno}
              </MDTypography>

              {campanha.evento && (
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  Evento: {campanha.evento.titulo}
                </MDTypography>
              )}
              {campanha.filtroCidade && (
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  Cidade: {campanha.filtroCidade}
                </MDTypography>
              )}
              {campanha.filtroEquipe && (
                <MDTypography variant="caption" color="text" display="block" mb={1}>
                  Equipe: {campanha.filtroEquipe}
                </MDTypography>
              )}

              <Grid container spacing={2} my={2}>
                {[
                  { label: "Destinatários", value: campanha.totalDestinatarios },
                  { label: "Enviados", value: campanha.totalEnviados },
                  { label: "Falhas", value: campanha.totalFalhas },
                  { label: "Ignorados", value: campanha.totalIgnorados },
                ].map((item) => (
                  <Grid item xs={6} sm={3} key={item.label}>
                    <MDBox
                      p={1.5}
                      borderRadius="lg"
                      sx={{ bgcolor: "grey.100", textAlign: "center" }}
                    >
                      <MDTypography variant="h5" fontWeight="bold">
                        {item.value}
                      </MDTypography>
                      <MDTypography variant="caption">{item.label}</MDTypography>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>

              {campanha.erroResumo && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {campanha.erroResumo}
                </Alert>
              )}

              <MDTypography variant="caption" color="text" display="block" mb={2}>
                Criado em {formatDate(campanha.createAt, "dd/MM/yyyy HH:mm")}
                {campanha.criadoPor ? ` por ${campanha.criadoPor.nome}` : ""}
              </MDTypography>

              <MDBox mb={2} p={2} sx={{ bgcolor: "grey.50", borderRadius: 2 }}>
                <MDTypography variant="button" fontWeight="medium" mb={1}>
                  Template
                </MDTypography>
                {campanha.assuntoEmail && (
                  <MDTypography variant="caption" display="block">
                    Assunto: {campanha.assuntoEmail}
                  </MDTypography>
                )}
                {campanha.tituloPush && (
                  <MDTypography variant="caption" display="block">
                    Push: {campanha.tituloPush}
                  </MDTypography>
                )}
                <MDTypography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", mt: 1 }}
                >
                  {campanha.corpoTemplate}
                </MDTypography>
              </MDBox>

              {podeCancelar && (
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirmCancel(true)}
                  disabled={canceling}
                  sx={{ mb: 2 }}
                >
                  Cancelar campanha
                </MDButton>
              )}

              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Log de envios
              </MDTypography>
              <LojaDataGrid
                rows={envios}
                columns={envioColumns}
                loading={loadingEnvios}
                getRowId={(row) => row.id}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                disableRowSelectionOnClick
              />
            </>
          ) : null}
        </MDBox>
      </Drawer>

      <ConfirmDialog
        open={confirmCancel}
        title="Cancelar campanha"
        message="Deseja cancelar o envio desta campanha? Os envios já realizados não serão revertidos."
        confirmLabel="Cancelar campanha"
        onConfirm={handleCancel}
        onClose={() => setConfirmCancel(false)}
      />
    </>
  );
}
